import axios from 'axios';
import { useCallback, useEffect } from 'react';

import { AuthService } from '@/services';
import { UserData } from '@/types';

import { useAuthState } from './AuthStateContext';
import { useError } from './ErrorContext';

export function useAuth() {
  const { state, dispatch } = useAuthState();
  const { handleApiError } = useError();

  const fetchCurrentUser = useCallback(async () => {
    try {
      console.log('fetching current user');
      dispatch({ type: 'SET_LOADING', payload: true });
      const authResponse = await AuthService.getCurrentUser();
      console.log('authResponse', authResponse);
      dispatch({ type: 'SET_USER', payload: authResponse.user });
    } catch (error) {
      if (axios.isAxiosError(error)) {
        if (error.response?.status === 401) {
          // Don't redirect if already on login page
          if (!window.location.pathname.startsWith('/login')) {
            window.location.href = '/login';
          }
        } else {
          const message = 'Failed to fetch user';
          dispatch({ type: 'SET_ERROR', payload: message });
          handleApiError(error, 'Authenticate');
        }
      } else {
        const message = 'Failed to fetch user';
        dispatch({ type: 'SET_ERROR', payload: message });
        handleApiError(error, 'Authenticate');
      }
    }
  }, [dispatch, handleApiError]);

  const login = useCallback(async (credentials: { email: string; password: string }): Promise<UserData> => {
    try {
      dispatch({ type: 'SET_LOADING', payload: true });
      const authResponse = await AuthService.login(credentials);
      dispatch({ type: 'SET_USER', payload: authResponse.user });
      return authResponse.user;
    } catch (error) {
      const message = 'Failed to login';
      dispatch({ type: 'SET_ERROR', payload: message });
      handleApiError(error, 'Login');
      throw error;
    }
  }, [dispatch, handleApiError]);

  const register = useCallback(async (userData: {
    email: string;
    password: string;
    name?: string;
  }): Promise<UserData> => {
    try {
      dispatch({ type: 'SET_LOADING', payload: true });
      const authResponse = await AuthService.register(userData);
      dispatch({ type: 'SET_USER', payload: authResponse.user });
      return authResponse.user;
    } catch (error) {
      const message = 'Failed to register';
      dispatch({ type: 'SET_ERROR', payload: message });
      handleApiError(error, 'Register');
      throw error;
    }
  }, [dispatch, handleApiError]);

  const logout = useCallback(async (): Promise<void> => {
    try {
      await AuthService.logout();
      dispatch({ type: 'LOGOUT' });
    } catch (error) {
      // Even if the API call fails, we should still log out locally
      dispatch({ type: 'LOGOUT' });
      handleApiError(error, 'Logout');
    }
  }, [dispatch, handleApiError]);

  const clearError = useCallback(() => {
    dispatch({ type: 'CLEAR_ERROR' });
  }, [dispatch]);

  // Auto-fetch current user on mount if token exists
  useEffect(() => {
    const token = localStorage.getItem('token');
    console.log('token', token, 'state.user', state.user, 'state.loading', state.loading);
    if (token && !state.user && !state.loading) {
      fetchCurrentUser();
    }
  }, [state.user, state.loading, fetchCurrentUser]);

  return {
    user: state.user,
    isAuthenticated: state.isAuthenticated,
    loading: state.loading,
    error: state.error,
    login,
    register,
    logout,
    fetchCurrentUser,
    clearError,
  };
}
